<template>
  <div
    id='pre-qualify-simulation-page'
    :style="{ backgroundImage: 'url(' + bgImage + ')' }"
    :class="{ mobile: isMobile() }"
  >
    <CloseButton dark sameBgInMobile />
    <div class="header" :class="{ mobile: isMobile() }">
      <Asset name="logo.svg"
        alt="Logo Parcelex"
        title="Logo Parcelex"
        class="logo"
      />
      <h1>
        Parcele suas compras<br/>
        online com <span>boletos</span><br/>
        em até <span>{{ maxInstallments }} vezes</span>
      </h1>
    </div>

    <div class="body">
      <h2>Simule sua compra</h2>
      <p v-show="!isValidPrice">
        Veja a estimativa dos seus pagamentos mensais
      </p>

      <div class="input-group">
        <label>Valor da compra</label>
        <div class="input-controller">
          <span class="unit" :class="{ mobile: isMobile() }">R$</span>
          <money
            class="money-input"
            :class="{ mobile: isMobile() }"
            v-model="currentPrice"
            v-bind="money"
            id="money-input"
          />
        </div>
      </div>

      <div class="message">
        <p class="message-without-price" v-if="currentPrice === 0">
          Mínimo de R$ 250,00
        </p>
        <p class="message-invalid-price" v-else-if="!isValidPrice">
          Insira um valor igual ou superior a R$ 250,00
        </p>
      </div>

      <DownPaymentList
        id="down-payment-list"
        :class="{ mobile: isMobile() }"
        v-show="isValidPrice"
        :indexSelected="downPaymentIndexSelected"
        @downPaymentChange="onDownpaymentChange"
        :downPaymentOptions="downPaymentOptions"
      />

      <h4 v-if="isValidPrice" :class="{ mobile: isMobile() }" >Veja sua simulação</h4>

      {{ downPaymentOptions ? null : 'carregando...' }}

      <InstallmentOptionList
        id="installment-option-list"
        v-show="isValidPrice"
        :loadingValues="installmentsSkeleton"
        :installmentOptions="installmentOptions"
      />

      <button
        v-if="!hasResult && isValidPrice"
        :disabled="!isValidPrice"
        @click="onBtnClick"
      >
        Verificar crédito
      </button>

      <div class="has-result" v-if="hasResult">
        <p v-if="translateErrorText" class="error-text">
          {{translateErrorText}}
        </p>

        <button class="close-btn" @click="handleCheckout">
          Continuar compra
        </button>

        <div class="close-simulation">
          <a @click="handleClose">
            Sair da simulação
          </a>
        </div>
      </div>

      <footer :class="{ mobile: isMobile() }">
        Esta simulação tem juros e CET de
        {{ installmentInterest | floatFilter }}%
        ao mês. A taxa de juros pode variar de acordo com o perfil de crédito.
        Um pagamento de entrada pode ser necessário
      </footer>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import { Money } from 'v-money';

import globals from '@/globals';

import UtilMixin from '@/mixins/util';
import StringMixin from '@/mixins/string';

import Asset from '@/components/common/Asset.vue';
import DownPaymentList from '@/components/common/DownPaymentList.vue';
import InstallmentOptionList from '@/components/common/InstallmentOptionList.vue';
import CloseButton from '../components/CloseButton.vue';
import UpdateIframeHeight from '../mixins/UpdateIframeHeight.vue';

export default {
  name: 'preQualify-simulation',
  mixins: [UtilMixin, StringMixin, UpdateIframeHeight],
  data: () => ({
    maxInstallments: globals().maxInstallments,
    money: {
      decimal: ',',
      thousands: '.',
      prefix: '',
      suffix: '',
      precision: 2,
      masked: false,
    },
  }),
  watch: {
    currentPrice() {
      setTimeout(() => this.onResize(document.body.clientHeight + 20), 500);
    },
  },
  computed: {
    ...mapState('preQualify', [
      'downPaymentIndexSelected',
      'downPaymentOptions',
      'installmentsSkeleton',
      'price',
    ]),
    ...mapGetters('preQualify', [
      'hasResult',
      'translateErrorText',
    ]),
    installmentOptions() {
      return (Array.isArray(this.downPaymentOptions)
        && this.downPaymentOptions.length > this.downPaymentIndexSelected)
        ? this.downPaymentOptions[this.downPaymentIndexSelected].installments : [];
    },
    installmentInterest() {
      return (Array.isArray(this.installmentOptions)
        && this.installmentOptions.length > this.downPaymentIndexSelected)
        ? this.installmentOptions[this.downPaymentIndexSelected].interest : 0;
    },
    ...mapGetters('preQualify', [
      'hasResult',
      'translateErrorText',
    ]),
    currentPrice: {
      get() {
        return this.price;
      },
      set(value) {
        if (value !== this.currentPrice) {
          this.updatePrice(value);
        }
      },
    },
    bgImage() {
      return 'https://cdn.parcelex.com.br/assets/v2/prequalify-bg.png';
    },
    isValidPrice() {
      return this.currentPrice >= 200;
    },
  },
  methods: {
    ...mapActions('preQualify', [
      'setDownPaymentIndex',
      'goToPage',
      'setContext',
      'close',
      'goToCheckout',
      'getPreview',
      'updatePrice',
      'onResize',
    ]),
    onDownpaymentChange(index) {
      if (index !== this.downPaymentIndexSelected) this.setDownPaymentIndex(index);
    },
    onBtnClick() {
      this.goToPage('prequalify-form');
    },
    handleCheckout() {
      this.goToCheckout();
    },
    handleClose() {
      this.close();
    },
  },
  async created() {
    await this.getPreview();
  },
  components: {
    Asset,
    Money,
    DownPaymentList,
    InstallmentOptionList,
    CloseButton,
  },
};
</script>

<style lang="scss" scoped>
.input-controller {
  position: relative;
}

.money-input {
  margin-bottom: 0;
  height: 38px;
  padding-left: 38px;
  &.mobile {
    width: 213px !important;
    height: 48px;
  }
}

.unit {
  position: absolute;
  display: block;
  top: 6px;
  left: 28px;
  font-size: 18px;
  line-height: 24px;
  color: black;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  &.mobile {
    left: 12px;
    top: 12px;
  }
}

#pre-qualify-simulation-page {
  position: relative;
  width: auto;
  min-height: 492px;

  background-size: 100% auto;
  background-repeat: no-repeat;
  background-position: center top;

  .header {
    height: 260px;
    padding-top: 24px;
    padding-left: 20px;
    margin-bottom: -10px;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0) 60%, #FFFFFF 90%, #FFFFFF 100%);
    &.mobile {
      background: linear-gradient(180deg, rgba(255, 255, 255, 0) 20%, #FFFFFF 90%, #FFFFFF 100%);
    }

    h1 {
      @extend .gray-700;
      margin-top: 24px;
      width: 359px;

      span {
        @extend .primary;
      }
    }
  }
  .body {
    text-align: center;
    padding: 0 20px;

    &.mobile {
      padding: 0;
    }

    h2 + p {
      margin-top: 8px;
    }

    h2 {
      @extend .gray-700;
    }

    h4 {
      @extend .gray-700;
      margin-top: 20px;
      &.mobile {
        font-size: 20px;
        line-height: 24px;
      }
    }

    p {
      @extend .small-regular;
      @extend .gray-400;
    }

    .message {
      margin-top: 8px;

      .message-without-price {
        @extend .small-regular;
        @extend .gray-400;
      }

      .message-invalid-price {
        @extend .small-regular;
        color: var(--danger);
      }
    }

    .input-group {
      margin-top: 16px;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      position: relative;

      label {
        @extend .body-regular;
        align-self: center;
      }

      .money-input {
        box-shadow: none;
        width: 141px;
        margin-left: 16px;

        + p {
          margin-top: 8px;
        }
      }
    }

    .error-text {
      padding: 0px 20px 20px 20px;
      color: var(--danger);
    }

    button {
      @extend .primary;
      margin-top: 0px;
    }

    .has-result {
      button {
        @extend .primary;
        margin: 24px 0px 16px 0px;
      }

      .close-simulation {
        a {
          @extend .primary;
          cursor: pointer;
        }
      }
    }

    footer {
      font-size: 14px;
      line-height: 20px;
      font-weight: 400;
      color: var(--gray-500);
      bottom: 0;
      padding: 0px 20px;
      margin: 24px 0px 20px 0px;
    }
  }

  &.mobile {
    width: 100%;
    background-size: 100% 180px;

    .header {
      height: 160px;

      h1 {
        width: 100%;
        line-height: 32px;
        font-size: 24px;
      }
    }

    .body {
      h2 {
        font-size: 20px;
        margin-top: 24px;
      }

      .input-group {
        flex-direction: column;

        label {
          width: 100%;
          margin-bottom: 8px;
        }

        .money-input {
          margin: 0;
          min-width: 60%;
        }
      }

      .error-text {
        padding: 20px 20px 0 20px;
      }

      button {
        margin-top: 20px;
        min-width: 87%;
        height: 48px;

        &:disabled {
          pointer-events: none;
        }
      }

      footer {
        margin: 20px 0px 24px 0px;
        text-align: left;
      }
    }
  }
}

#down-payment-list {
  margin-top: 20px;
  text-align: left;

  &.mobile {
    margin: 20px 24px 0 24px;
  }
}

#installment-option-list {
  margin: 28px 0 24px 0;

  &.mobile {
    margin: 20px 24px 0 24px;
  }
}
</style>
